import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/CopyButton';
import type { LockConflict } from '@/hooks/useObjectLock';

// ============================================================================
// LockConflictView — блокировка объекта другим пользователем (совместное
// редактирование): имя держателя/время окончания, «Попросить освободить»
// (любой) и «Отобрать блокировку» (админ). Внутренний releaseRequestStatus —
// чисто локальное состояние этого представления (SRP: родитель не читает его).
// Вынесено из AutoObjectForm (E2e9).
// ============================================================================

interface LockConflictViewProps {
  conflict: LockConflict;
  isAdmin: boolean;
  onRequestRelease: (lockId: string) => Promise<void>;
  onForceRelease: (lockId: string) => Promise<void>;
}

export function LockConflictView({ conflict, isAdmin, onRequestRelease, onForceRelease }: LockConflictViewProps) {
  const [status, setStatus] = useState<string | null>(null);
  const expires = conflict.expiresAt ? new Date(conflict.expiresAt).toLocaleString() : '';
  return (
    <div className="text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-md px-4 py-2 space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="font-medium">Объект заблокирован другим пользователем</div>
          <div className="text-xs">
            Пользователь: {conflict.userName} (до {expires})
          </div>
        </div>
        <CopyButton text={`Объект заблокирован: ${conflict.userName} (до ${expires})`} compact />
      </div>
      {status && (
        <div className="text-xs text-green-700 bg-green-50 rounded px-2 py-1">{status}</div>
      )}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={async () => {
            setStatus('Отправляем запрос…');
            try {
              await onRequestRelease(conflict.lockId);
              setStatus('Запрос отправлен. Подождите, пока держатель закроет объект.');
            } catch (e: any) {
              setStatus(e?.message || 'Не удалось отправить запрос');
            }
          }}
        >
          Попросить освободить
        </Button>
        {isAdmin && (
          <Button
            size="sm"
            variant="destructive"
            onClick={async () => {
              setStatus('Отбираем блокировку…');
              try {
                await onForceRelease(conflict.lockId);
                setStatus('Блокировка снята. Попробуйте сохранить снова.');
              } catch (e: any) {
                setStatus(e?.message || 'Не удалось отобрать блокировку');
              }
            }}
          >
            Отобрать блокировку
          </Button>
        )}
      </div>
    </div>
  );
}
